import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { luxuryHireApi, LuxuryVehicle, VehicleListResponse } from '../../../services/luxuryHireApi';

const CATEGORY_LABELS: Record<string, string> = {
  luxury_sedan: 'Luxury Sedan',
  sports_car: 'Sports Car',
  suv: 'Luxury SUV',
  bus: 'Luxury Bus',
  van: 'Executive Van',
  ultra_luxury: 'Ultra Luxury',
};

const CATEGORY_ICONS: Record<string, string> = {
  luxury_sedan: '🚗',
  sports_car: '🏎️',
  suv: '🚙',
  bus: '🚌',
  van: '🚐',
  ultra_luxury: '👑',
};

const formatCurrency = (amount: number, currency: string = 'NGN') => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const VehicleCatalog: React.FC = () => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState<LuxuryVehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchVehicles();
  }, [selectedCategory, searchQuery, page]);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const params: any = {
        page,
        per_page: 12,
        status: 'available',
      };

      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }

      if (searchQuery) {
        params.search = searchQuery;
      }

      const response: VehicleListResponse = await luxuryHireApi.getVehicles(params);
      setVehicles(response.vehicles);
      setTotalPages(response.total_pages);
      setError(null);
    } catch (err: any) {
      setError('Failed to load vehicles. Please try again.');
      console.error('Error fetching vehicles:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (vehicleId: number) => {
    navigate(`/luxury-hire/vehicle/${vehicleId}`);
  };

  const handleBookNow = (vehicleId: number) => {
    navigate(`/luxury-hire/book/${vehicleId}`);
  };

  const categories = ['all', 'luxury_sedan', 'sports_car', 'suv', 'ultra_luxury', 'bus', 'van'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="relative overflow-hidden bg-gradient-to-r from-amber-600 via-amber-500 to-orange-500 py-20">
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAzMHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-30" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 tracking-tight">
            Luxury Vehicle Hire
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto mb-8">
            Experience elegance on wheels. Premium vehicles with professional drivers for your special occasions.
          </p>
          
          <div className="max-w-xl mx-auto">
            <div className="relative">
              <input
                type="text"
                placeholder="Search vehicles..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                className="w-full px-6 py-4 rounded-full bg-white/95 backdrop-blur-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-4 focus:ring-amber-300/50 shadow-2xl text-lg"
              />
              <svg
                className="absolute right-6 top-1/2 -translate-y-1/2 w-6 h-6 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => {
                setSelectedCategory(category);
                setPage(1);
              }}
              className={`px-5 py-2.5 rounded-full font-medium transition-all duration-300 ${
                selectedCategory === category
                  ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/30 scale-105'
                  : 'bg-slate-700/50 text-slate-300 hover:bg-slate-600/50 hover:text-white'
              }`}
            >
              {category === 'all' ? (
                'All Vehicles'
              ) : (
                <>
                  <span className="mr-2">{CATEGORY_ICONS[category]}</span>
                  {CATEGORY_LABELS[category]}
                </>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-amber-500/30 rounded-full animate-spin border-t-amber-500"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl">🚗</span>
              </div>
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">😔</div>
            <p className="text-slate-400 text-lg">{error}</p>
            <button
              onClick={fetchVehicles}
              className="mt-4 px-6 py-2 bg-amber-500 text-white rounded-full hover:bg-amber-600 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : vehicles.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">🔍</div>
            <p className="text-slate-400 text-lg">No vehicles found matching your criteria.</p>
            <button
              onClick={() => {
                setSelectedCategory('all');
                setSearchQuery('');
              }}
              className="mt-4 px-6 py-2 bg-amber-500 text-white rounded-full hover:bg-amber-600 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {vehicles.map((vehicle) => (
                <div
                  key={vehicle.id}
                  className="group bg-gradient-to-b from-slate-800 to-slate-800/50 rounded-2xl overflow-hidden border border-slate-700/50 hover:border-amber-500/50 transition-all duration-500 hover:shadow-2xl hover:shadow-amber-500/10 hover:-translate-y-2"
                >
                  <div className="relative h-56 overflow-hidden bg-slate-700">
                    {vehicle.thumbnail_url || (vehicle.photos && vehicle.photos.length > 0) ? (
                      <img
                        src={vehicle.thumbnail_url || vehicle.photos?.[0]}
                        alt={vehicle.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-700 to-slate-800">
                        <span className="text-6xl opacity-50">
                          {CATEGORY_ICONS[vehicle.vehicle_category] || '🚗'}
                        </span>
                      </div>
                    )}
                    
                    {vehicle.is_featured && (
                      <div className="absolute top-4 left-4 px-3 py-1 bg-amber-500 text-white text-sm font-medium rounded-full shadow-lg">
                        Featured
                      </div>
                    )}
                    
                    {vehicle.security_required && (
                      <div className="absolute top-4 right-4 px-3 py-1 bg-slate-900/80 text-amber-400 text-sm font-medium rounded-full backdrop-blur-sm">
                        Security Included
                      </div>
                    )}

                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>

                  <div className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="text-amber-400 text-sm font-medium uppercase tracking-wider">
                          {vehicle.brand}
                        </p>
                        <h3 className="text-xl font-bold text-white mt-1">{vehicle.name}</h3>
                      </div>
                      <div className="text-right">
                        <p className="text-slate-400 text-xs">From</p>
                        <p className="text-amber-400 font-bold text-lg">
                          {formatCurrency(vehicle.hourly_rate, vehicle.currency)}
                          <span className="text-slate-400 text-sm font-normal">/hr</span>
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-700/50 text-slate-300">
                        {CATEGORY_LABELS[vehicle.vehicle_category] || vehicle.vehicle_category}
                      </span>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-700/50 text-slate-300">
                        <svg className="w-3.5 h-3.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                        </svg>
                        {vehicle.passenger_capacity} seats
                      </span>
                      {vehicle.year && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-700/50 text-slate-300">
                          {vehicle.year}
                        </span>
                      )}
                    </div>

                    {vehicle.features && vehicle.features.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-4">
                        {vehicle.features.slice(0, 3).map((feature, index) => (
                          <span
                            key={index}
                            className="text-xs text-slate-400 bg-slate-800 px-2 py-0.5 rounded"
                          >
                            {feature}
                          </span>
                        ))}
                        {vehicle.features.length > 3 && (
                          <span className="text-xs text-amber-400">
                            +{vehicle.features.length - 3} more
                          </span>
                        )}
                      </div>
                    )}

                    <div className="flex gap-3 mt-auto">
                      <button
                        onClick={() => handleViewDetails(vehicle.id)}
                        className="flex-1 px-4 py-2.5 border border-slate-600 text-slate-300 rounded-xl font-medium hover:bg-slate-700 hover:border-slate-500 transition-colors"
                      >
                        View Details
                      </button>
                      <button
                        onClick={() => handleBookNow(vehicle.id)}
                        className="flex-1 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-medium hover:from-amber-600 hover:to-orange-600 transition-colors shadow-lg shadow-amber-500/25"
                      >
                        Book Now
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-12">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 rounded-lg bg-slate-700 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600 transition-colors"
                >
                  Previous
                </button>
                
                <div className="flex gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                        page === p
                          ? 'bg-amber-500 text-white'
                          : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
                
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 rounded-lg bg-slate-700 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600 transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <div className="bg-gradient-to-r from-slate-800 to-slate-800/50 border-t border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-2xl font-bold text-white text-center mb-10">
            Why Choose Our Luxury Fleet?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🎖️</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Professional Drivers</h3>
              <p className="text-slate-400">
                Licensed, trained, and background-checked chauffeurs for your safety.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🛡️</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Security Available</h3>
              <p className="text-slate-400">
                Optional security personnel for VIP protection and peace of mind.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">✨</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Premium Experience</h3>
              <p className="text-slate-400">
                Immaculate vehicles, refreshments, and personalized service.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleCatalog;
